/**
 * La scheda di una serie: stagioni ed episodi.
 *
 * ## Perché non arriva con il listino
 *
 * Perché il listino porta **un titolo per serie** e le stagioni stanno in una chiamata a sé
 * (`get_series_info`). Chiederle per ogni piastrella di una fascia vorrebbe dire cinquanta richieste
 * per disegnare uno schermo, sul gateway che conta le richieste per indirizzo e blocca chi esagera —
 * lo stesso meccanismo che a fine settembre ha fatto bandire venti volte in una sera un indirizzo di
 * casa. Si chiede quando si apre la scheda, cioè una volta per serie e solo se qualcuno la guarda.
 *
 * ## Il numero della stagione non è la sua posizione
 *
 * Un catalogo importato salta le stagioni: capita di avere la 2 e la 4 e non la 3, perché il
 * fornitore ne vende due su quattro. Quindi la stagione si identifica col **suo numero**, e non
 * con l'indice nell'elenco: confonderli aprirebbe l'episodio di un'altra stagione, cioè uno
 * spoiler consegnato da un errore di indicizzazione.
 */

import {
  WATCHED_PERCENT,
  episodeProgressKey,
  type VodItem,
  type WatchProgress,
} from './catalog.js';

export interface EpisodeInfo {
  /** L'identificativo con cui si chiede la riproduzione al gateway. */
  readonly id: string;
  readonly season: number;
  readonly episode: number;
  readonly title: string;
  readonly description: string;
  readonly durationMin: number | null;
  readonly imageUrl: string | null;
}

export interface SeriesSeason {
  readonly number: number;
  readonly episodes: ReadonlyArray<EpisodeInfo>;
}

/**
 * Quel che TMDB sa di un titolo, e che stava in tabella senza uscire da nessuna parte.
 *
 * Trama, voto, durata, frase di lancio, immagine larga: sono i campi per cui una scheda esiste. La
 * scheda di un film ne mostrava **nessuno**, perché l'app non chiedeva mai `get_vod_info`; quella di
 * una serie mostrava solo la trama, perché gli altri il backend li dichiarava vuoti.
 *
 * `null` dove il dato manca, e non zero: un film senza voto e un film votato zero sono due cose
 * diverse, e disegnare «⭐ 0,0» su tutto il catalogo sarebbe peggio di non disegnare niente.
 */
export interface TitleFacts {
  readonly plot: string;
  readonly posterUrl: string | null;
  /** L'immagine larga 16:9: è la key art della scheda, dove il prototipo mette il tratteggio. */
  readonly backdropUrl: string | null;
  readonly year: number;
  readonly genre: string;
  readonly durationMin: number | null;
  /** Il voto TMDB, da 0 a 10. */
  readonly rating: number | null;
  readonly voteCount: number | null;
  readonly tagline: string;
  /**
   * La scheda tecnica, per il pannello Info (03-SCHERMATE-TV §3.12).
   *
   * `null` dove il dato non c'è, e il pannello **non disegna la riga**: una riga «REGIA» vuota si
   * legge come un dato che non è arrivato, mentre l'assenza dice che quel catalogo non lo ha. Sono
   * due cose diverse, e la seconda è quella vera.
   *
   * Regia e cast arrivano dalla sorgente e per mesi sono stati buttati all'import — vedi la
   * migrazione `CatalogKeepsDirectorAndCast`. Paese, sottotitoli e classificazione d'età **la
   * sorgente non li manda**, e finché non li manda non compaiono.
   */
  readonly director: string | null;
  readonly cast: string | null;
}

export interface SeriesDetail extends TitleFacts {
  readonly id: string;
  readonly title: string;
  /** In ordine di numero crescente, e **senza buchi riempiti**: se manca la 3, manca. */
  readonly seasons: ReadonlyArray<SeriesSeason>;
}

/**
 * La scheda di un film: gli stessi fatti, senza stagioni.
 *
 * Un tipo a sé e non `SeriesDetail` con `seasons` vuoto: «un film senza stagioni» e «una serie di cui
 * non sono arrivate le stagioni» si scriverebbero uguale, e sono due cose che la schermata tratta in
 * modo diverso — la seconda dice «sto caricando», la prima no.
 */
export interface MovieDetail extends TitleFacts {
  readonly id: string;
  readonly title: string;
}

/**
 * Il titolo del listino con le stagioni della scheda dentro.
 *
 * Serve perché `watchStateOf` conta gli episodi da `episodesPerSeason`, e un titolo appena arrivato
 * dal listino ce l'ha vuoto: senza questo passaggio la scheda di una serie guardata per metà
 * direbbe «mai iniziata», che è il difetto peggiore di tutti — dice a chi guarda che il suo
 * avanzamento è andato perso.
 *
 * **Appiattisce per posizione, non per numero.** `episodesPerSeason` è un elenco indicizzato da
 * zero, ed è quel che `watchStateOf` sa leggere: la stagione 4 di una serie che ha solo la 2 e la 4
 * sta in posizione 1. Il lato che conosce i numeri veri è `episodeAt`, qui sotto, e sono le due metà
 * della stessa conversione — la posizione serve al conteggio, il numero serve ad aprire l'episodio.
 */
export function withSeasons(item: VodItem, detail: SeriesDetail): VodItem {
  return { ...item, episodesPerSeason: detail.seasons.map((season) => season.episodes.length) };
}

/**
 * L'episodio in una data posizione appiattita, con il suo numero di stagione **vero**.
 *
 * È il ritorno da `watchStateOf` — che parla per posizioni — a quel che serve per riprodurre.
 */
export function episodeAt(detail: SeriesDetail, seasonIndex: number, episodeNumber: number): EpisodeInfo | null {
  const season = detail.seasons[seasonIndex];
  if (!season) return null;
  return season.episodes.find((entry) => entry.episode === episodeNumber) ?? null;
}

/** Quanto si è visto di un episodio, 0 se mai toccato. */
export function episodePercent(
  progress: WatchProgress,
  seriesId: string,
  episode: EpisodeInfo,
): number {
  return progress.episodes[seriesId]?.[episodeProgressKey(episode.season, episode.episode)] ?? 0;
}

/**
 * Da dove si riprende una serie: il primo episodio non finito, in ordine di stagione ed episodio.
 *
 * Se tutti sono finiti torna `null` — **non** il primo, che sarebbe «ricomincia da capo» travestito
 * da «riprendi». La differenza si vede sul tasto principale della scheda, e prometterne una per
 * l'altra è il genere di errore che fa perdere il punto in cui si era arrivati.
 */
export function resumePoint(detail: SeriesDetail, progress: WatchProgress): EpisodeInfo | null {
  for (const season of detail.seasons) {
    for (const episode of season.episodes) {
      if (episodePercent(progress, detail.id, episode) < WATCHED_PERCENT) return episode;
    }
  }
  return null;
}

/**
 * L'episodio che segue, o `null` alla fine della serie: attraversa il confine fra stagioni.
 *
 * Scritto con due cicli e non con `flatMap`: questo pacchetto compila anche contro la libreria più
 * vecchia fra quelle dei bersagli, e `flatMap` non c'è. È il genere di dettaglio che si scopre solo
 * compilando per il televisore, cioè tardi.
 */
export function episodeAfter(detail: SeriesDetail, current: EpisodeInfo): EpisodeInfo | null {
  const flat: EpisodeInfo[] = [];
  for (const season of detail.seasons) {
    for (const episode of season.episodes) flat.push(episode);
  }
  const at = flat.findIndex(
    (entry) => entry.season === current.season && entry.episode === current.episode,
  );
  if (at < 0) return null;
  return flat[at + 1] ?? null;
}
